import { useEffect, useLayoutEffect } from 'react';

import { MdError } from 'react-icons/md';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  useLocation,
} from 'react-router-dom';

import { DEMO_BASENAME, DEMO_FEATURE_ROUTE_PREFIX, isDemoZone } from './components/demo/constants';
import InsightsHubLayout from './components/InsightsHubLayout';
import MessagePage from './components/MessagePage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { CategoryRedirect } from './pages/insightsHub/CategoryRedirect';
import { ProjectScreenerGuard } from './pages/projectScreener/ProjectScreenerGuard';
import { compassProjectTypeChildren } from './routes/children/compassRoutes';
import { projectDetailChildren } from './routes/children/projectRoutes';
import {
  AccountRequest,
  Compass,
  Contact,
  DeveloperPortal,
  FreeDashboard,
  Home,
  MyPlan,
  MyAdvisoryRequests,
  DeepDivePicker,
  InsightsHub,
  InsightsHubArticle,
  InsightsHubCorsia,
  InsightsHubIcvcm,
  InsightsHubMarketUpdate,
  InsightsHubMarketUpdatesList,
  InsightsHubPinnedPost,
  InsightsHubResearchUpdate,
  InsightsHubResearchUpdatesList,
  InsightsHubSearchResults,
  LazyRoute,
  NewScreening,
  Programs,
  Project,
  ProjectScreener,
  ProjectType,
  RatingScalesExplained,
  Register,
  RetireeInsights,
  RetireeInsightsDrillDown,
  ScreenerFAQ,
  CoveredMethodologies,
  ScreeningResults,
  Search,
  SignUp,
  SupplyLink,
  UpcomingRatingFAQ,
  Upgrade,
  WhereToBuy,
} from './routes/lazyImports';
import { page } from './services/analytics';
import { withFaroRouterInstrumentation } from './services/faro/faroRouter';
import Sentry from './services/sentry';
import { useGlobalStore } from './stores/useGlobalStore';
import { absorbCacheBustParam } from './utils/lazyWithReload';

// Strip the stale-chunk recovery cache-bust param BEFORE the router snapshots
// window.location below, so `?_sbust` never leaks into react-router state / the
// address bar / router-sourced telemetry after a recovery reload.
absorbCacheBustParam();

let previousPath = '/';
const NavigateToProfileSettings = () => {
  useLayoutEffect(() => {
    window.location.href = process.env.IS_PRODUCTION_APP
      ? 'https://auth.calyxglobal.com/account'
      : 'https://auth.calyxstaging.com/account';
  }, []);
  return <></>;
};

const AppLayout = () => {
  const location = useLocation();

  useEffect(() => {
    page();
    if (!(location.state as { preventScrollReset?: boolean })?.preventScrollReset) {
      const layout = document.getElementById('layout');
      layout?.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <MessagePage
          title="Oops, that's our bad!"
          description="We're not exactly sure what happened, rest assured we've been notified about this issue and this will be fixed soon!"
          Icon={MdError}
          hasAction
          resetError={resetError}
        />
      )}
    >
      <ScrollRestoration />
      <Outlet />
    </Sentry.ErrorBoundary>
  );
};

const loaderCallback = ({ request }) => {
  const prev = previousPath;
  previousPath = new URL(request.url).pathname;
  return { prevPath: prev };
};

// In the demo zone the app root lands the prospect on their granted feature
// (all URLs are already under /demo via the router basename); otherwise Home.
const HomeOrDemoFeature = () => {
  const demoSession = useGlobalStore((s) => s.demoSession);
  if (isDemoZone && demoSession) {
    return <Navigate to={DEMO_FEATURE_ROUTE_PREFIX[demoSession.feature_key]} replace />;
  }
  return <LazyRoute Component={Home} />;
};

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <AppLayout />,
      errorElement: (
        <MessagePage
          title='Page not found'
          description='The page you are looking for doesn’t exist or has been moved.'
          hasAction
        />
      ),
      children: [
        {
          path: '/',
          element: <ProtectedRoute />,
          children: [
            {
              path: `/`,
              element: <HomeOrDemoFeature />,
            },
            {
              path: '/login',
              element: <Navigate to='/' />,
            },
            {
              path: '/developer-portal',
              element: <LazyRoute Component={DeveloperPortal} />,
            },
            {
              path: 'research-hub',
              element: <InsightsHubLayout />,
              children: [
                {
                  path: '',
                  element: <LazyRoute Component={InsightsHub} />,
                },
                {
                  path: 'search',
                  element: <LazyRoute Component={InsightsHubSearchResults} />,
                },
                {
                  path: 'corsia',
                  element: <LazyRoute Component={InsightsHubCorsia} />,
                },
                {
                  path: 'icvcm-ccp',
                  element: <LazyRoute Component={InsightsHubIcvcm} />,
                },
                {
                  path: 'research-updates',
                  element: <LazyRoute Component={InsightsHubResearchUpdatesList} />,
                },
                {
                  path: 'market-updates',
                  element: <LazyRoute Component={InsightsHubMarketUpdatesList} />,
                },
                {
                  path: 'market-update/:slug',
                  element: <LazyRoute Component={InsightsHubMarketUpdate} />,
                },
                {
                  path: 'pinned/:slug',
                  element: <LazyRoute Component={InsightsHubPinnedPost} />,
                },
                {
                  path: ':category',
                  element: <CategoryRedirect />,
                },
                {
                  path: ':category/:slug',
                  element: <LazyRoute Component={InsightsHubArticle} />,
                },
                {
                  path: 'research_update/:slug',
                  element: <LazyRoute Component={InsightsHubResearchUpdate} />,
                },
              ],
            },
            // redirect old blog/resources/notes to research-hub start
            {
              path: 'blog_detail_dist',
              element: (
                <Navigate
                  to={`/research-hub/pinned/current-calyx-risk-ratings-distribution${window.location.search}`}
                  replace
                />
              ),
            },
            {
              path: 'blog',
              element: <Navigate to={`/research-hub${window.location.search}`} replace />,
            },
            {
              path: 'resource',
              element: <Navigate to={`/research-hub${window.location.search}`} replace />,
            },
            {
              path: 'note',
              element: <Navigate to={`/research-hub${window.location.search}`} replace />,
            },
            // redirect old blog/resources/notes to research-hub end
            {
              path: `/credit-ratings`,
              element: <LazyRoute Component={Search} />,
              loader: loaderCallback,
            },
            {
              path: '/upcoming-ratings/faq',
              element: <LazyRoute Component={UpcomingRatingFAQ} />,
            },
            // redirect to new search page
            {
              path: `/search`,
              element: <Navigate to={`/credit-ratings`} replace />,
              loader: loaderCallback,
            },
            {
              path: `/supply-link`,
              element: <Navigate to={`/supply-bridge`} replace />,
            },
            // redirect to supply bridge
            {
              path: `/supply-bridge`,
              element: <LazyRoute Component={SupplyLink} />,
            },
            {
              path: `/compass`,
              element: <LazyRoute Component={Compass} />,
            },
            {
              path: '/compass/:projectType',
              element: <LazyRoute Component={ProjectType} />,
              children: compassProjectTypeChildren,
            },
            // SCRUM-1513: Expert advisory services pages were removed; send old
            // links and bookmarks (incl. /expert-advisory/:service_name) home.
            {
              path: `/expert-advisory/*`,
              element: <Navigate to='/' replace />,
            },
            {
              path: '/project-screener',
              element: <ProjectScreenerGuard />,
              children: [
                {
                  index: true,
                  element: <LazyRoute Component={ProjectScreener} />,
                },
                {
                  path: 'new',
                  element: <LazyRoute Component={NewScreening} />,
                },
                {
                  path: 'new/faq',
                  element: <LazyRoute Component={ScreenerFAQ} />,
                },
                {
                  path: 'covered-methodologies',
                  element: <LazyRoute Component={CoveredMethodologies} />,
                },
                {
                  path: ':id',
                  element: <LazyRoute Component={ScreeningResults} />,
                },
              ],
            },
            {
              path: `/my-plan`,
              element: <LazyRoute Component={MyPlan} />,
            },
            {
              path: `/my-plan/deep-dive-requests`,
              element: <LazyRoute Component={DeepDivePicker} />,
            },
            {
              path: `/my-advisory-requests`,
              element: <LazyRoute Component={MyAdvisoryRequests} />,
            },
            {
              path: `/`,
              element: <LazyRoute Component={Project} />,
              children: projectDetailChildren(loaderCallback),
            },
            {
              path: `/where_to_buy`,
              element: <LazyRoute Component={WhereToBuy} />,
            },
            {
              path: 'ratings-scales-explained',
              element: <LazyRoute Component={RatingScalesExplained} />,
            },
            {
              path: 'retirement_insights',
              element: <LazyRoute Component={RetireeInsights} />,
            },
            {
              path: 'retirement_insights/:retiree',
              element: <LazyRoute Component={RetireeInsightsDrillDown} />,
            },
            {
              path: 'contact',
              element: <LazyRoute Component={Contact} />,
            },
            {
              path: 'profile',
              element: <NavigateToProfileSettings />,
            },
            {
              path: 'dashboard',
              element: <Navigate to='/' />,
            },
            {
              path: '/programs',
              element: <LazyRoute Component={Programs} />,
            },
            {
              path: '/upgrade',
              element: <LazyRoute Component={Upgrade} />,
            },
          ],
        },
        {
          path: '/signup',
          element: <LazyRoute Component={SignUp} />, // account signup form for secondnature universities
        },
        {
          path: '/account-signup',
          element: <LazyRoute Component={SignUp} signupType='b2b2b' />, // generic account sign up form for all users coming from B2B2B
        },
        {
          path: '/account-request',
          element: <LazyRoute Component={AccountRequest} />,
        },
        {
          path: '/register',
          element: <LazyRoute Component={Register} />,
        },
        {
          path: '/free-dashboard',
          element: <LazyRoute Component={FreeDashboard} />,
        },
      ],
    },
  ],
  { basename: isDemoZone ? DEMO_BASENAME : undefined }
);

// Initialize Faro with router instrumentation
const instrumentedRouter = withFaroRouterInstrumentation(router);

export const Routes = () => {
  return <RouterProvider router={instrumentedRouter} />;
};
